import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Avatar, Typography, ListItemButton, Divider } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

function NotificationItem({ notification, handleClose }) {
  const navigate = useNavigate();

  const handleClick = () => {
    handleClose?.();
    navigate('/dashboard/notifications', { state: { notificationId: notification._id } });
  };

  // Get user info from notification
  const user = notification?.from;
  const userName = user ? `${user.fname || ''} ${user.lname || ''}`.trim() : 'System';
  const userImage = user?.image;
  const userInitials = user ? `${user.fname?.[0] || ''}${user.lname?.[0] || ''}`.toUpperCase() : 'S';

  // Format timestamp
  const timeAgo = notification?.timestamp
    ? formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })
    : '';

  // Truncate text with ellipsis
  const truncateText = (text, maxLength = 60) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        sx={{
          py: 2,
          px: 2.5,
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'action.hover',
          },
          bgcolor: notification?.isRead ? 'transparent' : 'action.selected',
        }}
      >
        <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
          {/* Avatar */}
          <Avatar
            src={userImage}
            alt={userName}
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'primary.main',
              border: '2px solid',
              borderColor: notification?.isRead ? 'transparent' : 'primary.main',
            }}
          >
            {!userImage && (
              user ? (
                userInitials
              ) : (
                <AccountCircleIcon sx={{ fontSize: 32, color: 'white' }} />
              )
            )}
          </Avatar>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Title with ellipsis */}
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: notification?.isRead ? 500 : 600,
                color: 'text.primary',
                mb: 0.5,
                fontSize: '14px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
            >
              {notification?.title || 'Notification'}
            </Typography>

            {/* Description with ellipsis */}
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '13px',
                lineHeight: 1.5,
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                maxHeight: '40px',
              }}
            >
              {truncateText(notification?.message, 80)}
            </Typography>

            {/* Time and user info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.disabled',
                  fontSize: '11px',
                }}
              >
                {timeAgo}
              </Typography>
              {user && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                      fontSize: '11px',
                    }}
                  >
                    •
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                      fontSize: '11px',
                      fontWeight: 500,
                    }}
                  >
                    {userName}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {/* Unread indicator */}
          {!notification?.isRead && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                mt: 1,
                flexShrink: 0,
              }}
            />
          )}
        </Box>
      </ListItemButton>
      <Divider sx={{ borderColor: 'divider', opacity: 0.5 }} />
    </>
  );
}

export default NotificationItem;
